/**
 * Grupo de un día operativo en el historial (006 · T031; contracts/staff-ui.md §4, data-model §2.2).
 *
 * Encabezado "Hoy"/"Ayer"/fecha larga (`dayLabel`, con `today` de la respuesta del servidor — nunca
 * la fecha del teléfono, wiring 006 §0.3) y, si el día tiene algún par completo, sus horas brutas
 * (`worked_minutes`, `formatWorkedMinutes`). Las marcas ya llegan en orden cronológico ascendente
 * (R-8, `ORDER BY server_timestamp, id`): este componente no las reordena.
 *
 * 008 · T097 (contracts/ui.md §2.2): con `schedule` (el día de `GET /attendance/days`), bajo el
 * encabezado van el estado del día (badge con icono y texto) y el turno programado
 * (`features/schedule/day-status.tsx`). Un día evaluado sin marcas (falta, descanso, feriado…) llega
 * con `marks` vacío: se pinta solo el encabezado y su estado.
 *
 * 008 · T111: `pendingJustificationKinds` (tipos con una justificación en revisión ese día) y
 * `readOnly`/`online` pasan a `DayStatus` para "Justificación en revisión" y "Justificar".
 */
import type {
  JustificationKind,
  StaffAttendanceDay,
  StaffDay,
  StaffRegularizationItem,
} from '@fermin/contracts';

import { dayLabel, formatWorkedMinutes } from '../../i18n';
import { DayStatus } from '../schedule/day-status';

import { MarkRow } from './mark-row';

export interface DayGroupProps {
  day: StaffAttendanceDay;
  /** Día operativo actual en la zona de la sede principal (mismo campo `today` de la respuesta). */
  today: string;
  /**
   * 007 · T036: regularizaciones por `anchor.mark_id`; `undefined` mientras no se cargaron (la fila
   * no pinta nada de la 007).
   */
  regularizations?: ReadonlyMap<string, StaffRegularizationItem>;
  /** Empresa suspendida: estados sin acciones. */
  readOnly?: boolean;
  online?: boolean;
  /** 008 · T097: el mismo día en `GET /attendance/days` (estado y turno); sin él, nada nuevo. */
  schedule?: StaffDay;
  /** 008 · T111: tipos con una justificación `PENDING` de este día. */
  pendingJustificationKinds?: readonly JustificationKind[];
}

export function DayGroup({
  day,
  today,
  regularizations,
  readOnly,
  online,
  schedule,
  pendingJustificationKinds,
}: DayGroupProps) {
  return (
    <section
      data-testid="history-day"
      data-day={day.operative_day}
      className="flex flex-col gap-fm-2"
    >
      <div className="flex items-baseline justify-between gap-fm-2">
        <h2 className="font-display text-fm-base font-bold text-fm-text">
          {dayLabel(day.operative_day, today)}
        </h2>
        {day.worked_minutes !== null ? (
          <span data-testid="history-day-worked" className="text-fm-sm text-fm-text-secondary">
            {formatWorkedMinutes(day.worked_minutes)}
          </span>
        ) : null}
      </div>
      {schedule !== undefined ? (
        <DayStatus
          day={schedule}
          {...(pendingJustificationKinds !== undefined
            ? { pendingKinds: pendingJustificationKinds }
            : {})}
          {...(readOnly !== undefined ? { readOnly } : {})}
          {...(online !== undefined ? { online } : {})}
        />
      ) : null}
      {day.marks.length > 0 ? (
        <ul className="flex flex-col divide-y divide-fm-border">
          {day.marks.map((mark) => (
            <MarkRow
              key={mark.id}
              mark={mark}
              {...(regularizations !== undefined
                ? { regularization: regularizations.get(mark.id) ?? null }
                : {})}
              {...(readOnly !== undefined ? { readOnly } : {})}
              {...(online !== undefined ? { online } : {})}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
